import { ApiError, me, type AnswerPayload, type MarkerPayload, type ServerSettings } from "./api";
import { ack, fail, markInFlight, peekPending, size } from "./outbox";
import { useAuthStore } from "../state/authStore";
import { useSyncStore } from "../state/syncStore";
import { setSyncTrigger } from "../state/store";

let draining = false;
let pendingDrain = false;
let trigger: ReturnType<typeof setTimeout> | null = null;

async function refreshSize(): Promise<void> {
  useSyncStore.getState().setOutboxSize(await size());
}

async function sendEntry(kind: string, payload: unknown): Promise<void> {
  if (kind === "answer") {
    await me.postAnswer(payload as AnswerPayload);
    return;
  }
  if (kind === "reset") {
    await me.reset();
    return;
  }
  if (kind === "settings") {
    await me.patchSettings(payload as Partial<ServerSettings>);
    return;
  }
  if (kind === "marker") {
    // Issue #123. Server-Validation (vocab_not_found) ist hier ein 4xx — wir
    // ack'en trotzdem in der drainOutbox-Schleife, weil ein 404 nicht durch
    // Retry-Loop heilbar ist (Vokabel ist halt nicht da). drainOutbox
    // unterscheidet zwischen unauthenticated (special) und allem anderen.
    await me.setMarker(payload as MarkerPayload);
    return;
  }
  throw new Error(`unknown outbox kind: ${kind}`);
}

/**
 * Drains the outbox in FIFO order. Returns silently when offline or when no
 * session exists. Concurrent calls coalesce: the in-flight call services one
 * extra pass after it finishes so a `triggerSyncSoon()` issued mid-drain isn't
 * lost.
 */
export async function drainOutbox(): Promise<void> {
  if (draining) {
    pendingDrain = true;
    return;
  }
  draining = true;
  try {
    do {
      pendingDrain = false;
      if (!useSyncStore.getState().isOnline) break;
      if (useAuthStore.getState().status !== "authed") break;

      const batch = await peekPending(50);
      if (batch.length === 0) break;

      useSyncStore.getState().setSyncState("syncing");
      await refreshSize();

      for (const entry of batch) {
        if (entry.id === undefined) continue;
        await markInFlight(entry.id);
        try {
          await sendEntry(entry.kind, entry.payload);
          await ack(entry.id);
        } catch (err) {
          if (err instanceof ApiError && err.kind === "unauthenticated") {
            await fail(entry.id, "unauthenticated");
            useAuthStore.getState().setGuest();
            useSyncStore.getState().setSyncState("error", "unauthenticated");
            return;
          }
          await fail(entry.id, err instanceof Error ? err.message : String(err));
          useSyncStore.getState().setSyncState("error", err instanceof Error ? err.message : String(err));
          return;
        }
      }
      await refreshSize();
    } while (pendingDrain);
    useSyncStore.getState().setSyncState("idle");
  } finally {
    draining = false;
    await refreshSize();
  }
}

/**
 * Called by the quiz store after each mutation. Coalesces into a single async
 * call so a rapid burst of answers doesn't fire many concurrent drains.
 */
export function triggerSyncSoon(): void {
  if (trigger !== null) clearTimeout(trigger);
  trigger = setTimeout(() => {
    trigger = null;
    void drainOutbox();
  }, 80);
}

let installed = false;

/**
 * Wires online/offline listeners + the quiz-store sync trigger + initial state.
 * Safe to call multiple times — only the first call has effect.
 */
export function installSyncManager(): void {
  if (installed) return;
  installed = true;

  setSyncTrigger(triggerSyncSoon);

  if (typeof window !== "undefined") {
    window.addEventListener("online", () => {
      useSyncStore.getState().setOnline(true);
      void drainOutbox();
    });
    window.addEventListener("offline", () => {
      useSyncStore.getState().setOnline(false);
      useSyncStore.getState().setSyncState("idle");
    });
  }

  // Snapshot the outbox size at startup so the banner is accurate before the
  // first answer.
  void refreshSize();
}
